import { Plugin, PluginKey } from "prosemirror-state";
import {EditorView} from "prosemirror-view";
import {NodeType, Schema, Slice} from "prosemirror-model";

export const KEY = new PluginKey("imageTransfer");

export type InsertMode = "DISABLED" | "DATA_URL" | "UPLOAD";

export interface ImageTransferOptions {
	mode?: InsertMode;         // default "dataUrl"
	upload?: (file: File) => Promise<string>; // pro "upload" režim
	toImageAttrs?: (file: File, src: string) => Record<string, any>; // mapování attrs
	onError?: (err: unknown, file?: File) => void;
}

export interface ImageTransferState {

}


const DEFAULT_OPTIONS:ImageTransferOptions = {
	mode: "DATA_URL",
	toImageAttrs: (file, src) => ({ src, alt: file.name, title: file.name, maxWidth: "100%" }),
	onError: () => {},
};


function getDropPos(view: EditorView, e: DragEvent): number {
	const pos = view.posAtCoords({ left: e.clientX, top: e.clientY });
	return pos ? pos.pos : view.state.selection.from;
}

function acceptedFile(f:File):boolean {
	return f.type.toLowerCase().startsWith("image/");
}

function readAsDataURL(file: File) {
	return new Promise<string>((resolve, reject) => {
		const r = new FileReader();
		r.onload = () => resolve(String(r.result || ""));
		r.onerror = reject;
		r.readAsDataURL(file);
	});
}

function ensureImageType(schema: Schema):NodeType {
	const imageType = schema.nodes.image;
	if (!imageType) throw new Error("Schema doesn't contain 'image' node.");
	return imageType;
}

function insertImageAt(view: EditorView, pos: number, attrs: Record<string, any>) {
	const imageType = ensureImageType(view.state.schema);
	// Bezpečně najdi poblíž validní pozici
	let $pos = view.state.doc.resolve(Math.max(0, Math.min(pos, view.state.doc.content.size)));
	const tr = view.state.tr.insert($pos.pos, imageType.create(attrs));
	view.dispatch(tr.scrollIntoView());
	// Nová pozice za právě vložený node:
	const inserted = tr.doc.nodeAt($pos.pos);
	const after = inserted ? $pos.pos + inserted.nodeSize : $pos.pos;
	return after;
}

async function handleFiles(files: File[], view: EditorView, basePos: number, ops: ImageTransferOptions) {
	// Insertin sequentionally; pos will be shifted after each node
	let insertPos = basePos;

	for (const file of files) {
		try {
			if (ops.mode === "DISABLED") continue;
			let src: string;
			if (ops.mode === "DATA_URL") {
				src = await readAsDataURL(file);
			} else if (ops.upload) {
				src = await ops.upload(file);
				if (src == "") {
					// Upload failed
					console.error("Upload failed for file (using data URL as a fallback):", file);
					src = await readAsDataURL(file);
				}
			}

			const attrs = ops.toImageAttrs(file, src);
			insertPos = insertImageAt(view, insertPos, attrs);
		} catch (err) {
			ops.onError?.(err, file);
		}
	}
}


export function imageTransferPlugin(customOpts: ImageTransferOptions = {}) {
	const options = { ...DEFAULT_OPTIONS, ...customOpts };

	return new Plugin<ImageTransferState>({
		key: KEY,
		props: {
			handlePaste(view:EditorView, e:ClipboardEvent, slice: Slice) {
				const dt = e.clipboardData;
				if (!dt) return false;

				// extrahuj Files z clipboardu (pozor: i items mohou obsahovat images)
				const files: File[] = [];
				for (const item of Array.from(dt.items || [])) {
					if (item.kind === "file") {
						const f = item.getAsFile();
						if (f && acceptedFile(f)) files.push(f);
					}
				}
				if (files.length == 0) {
					for (const f of Array.from(dt.files)) {
						if (acceptedFile(f)) files.push(f);
					}
				}

				if (files.length === 0) {
					return false;
				}

				e.preventDefault();

				const basePos = view.state.selection.from;
				console.log("My custom paste");
				handleFiles(files, view, basePos, options);
				return true;
			},

			handleDrop(view:EditorView, e:DragEvent, slice: Slice, moved:boolean) {
				if (moved) return false; // internal move - keep default behavior

				const dt = e.dataTransfer;
				if (!dt) return false;

				const files = Array.from(dt.files || []).filter(f => acceptedFile(f));
				if (files.length === 0) {
					// No image files => keep default behaviour (PM si inserts HTML/text if any)
					// Can handle url list here
					return false;
				}

				e.preventDefault();	// accepting the drop
				const pos = getDropPos(view, e);

				handleFiles(files, view, pos, options);

				return true;
			}
		}
	});
}